import React, {
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  ListSubheader,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CallMergeRoundedIcon from "@mui/icons-material/CallMergeRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { toast } from "react-toastify";

import {
  GetASAISubDepartments,
  GetCurrentShiftImportRecords,
  GetDepartmentShifts,
  SaveShiftRosterChanges,
} from "../../../services/asai";
import { GetUserDetail } from "../../../services/user";
import { GetAllDepartments } from "../../../services/admin";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import PageContainer from "../../../components/container/PageContainer";
import { GetAllEmployeeDetailsDropdowns } from "../../../services/human-resource";

type EmployeeType = "payroll" | "contract" | "cnc";
type ShiftHours = 8 | 10;

interface SubDepartmentOption {
  id: string;
  label: string;
  code: string;
}

interface DepartmentOption {
  id: string;
  label: string;
}

interface ShiftOption {
  id: string;
  label: string;
  shiftType: string;
  employeeType: EmployeeType;
  shiftHours: ShiftHours;
}

interface RosterEmployee {
  /** Stable frontend identity within the loaded monthly roster. */
  key: string;
  personId: string;
  personName: string;
  offDay: string;
  subDepartmentId: string;
  subDepartmentName: string;
  employeeType: EmployeeType;
  originalShiftId: string;
}

interface EmployeeDropdownOption {
  personId: string;
  personName: string;
  offDay?: string;
}

const EMPLOYEE_TYPE_OPTIONS: Array<{ value: EmployeeType; label: string }> = [
  { value: "payroll", label: "Payroll" },
  { value: "contract", label: "Contract" },
  { value: "cnc", label: "Apprentice" },
];

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: 7 },
  (_, index) => CURRENT_YEAR - 3 + index,
);

const normalizeText = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getApiField = (item: any, ...keys: string[]): any => {
  if (!item || typeof item !== "object") return undefined;

  // Prefer exact matches, then fall back to case-insensitive property matching.
  // ASP.NET DTOs and existing endpoints have used both camelCase and PascalCase.
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null) return item[key];
  }

  const wanted = new Set(keys.map((key) => key.toLowerCase()));
  const actualKey = Object.keys(item).find((key) =>
    wanted.has(key.toLowerCase()),
  );
  return actualKey ? item[actualKey] : undefined;
};

const isHrDepartment = (value: unknown): boolean => {
  const department = normalizeText(value);
  return (
    department === "hr" ||
    department === "h.r." ||
    department === "hr department" ||
    department === "human resources" ||
    department.includes("human resources") ||
    /^hr(?:\s|$|[-/&])/.test(department)
  );
};

const normalizeDepartmentOptions = (response: any): DepartmentOption[] => {
  const records = extractApiRecords(response);
  const options = records
    .map((item: any): DepartmentOption | null => {
      const id = String(
        getApiField(
          item,
          "id",
          "Id",
          "departmentId",
          "DepartmentId",
          "deptId",
          "DeptId",
        ) ?? "",
      ).trim();
      const label = String(
        getApiField(
          item,
          "departmentName",
          "DepartmentName",
          "name",
          "Name",
          "department",
          "Department",
        ) ?? "",
      ).trim();
      return id && label ? { id, label } : null;
    })
    .filter((item): item is DepartmentOption => item !== null);

  const unique = new Map<string, DepartmentOption>();
  options.forEach((item) => unique.set(item.id, item));
  return Array.from(unique.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
};

const parseShiftHours = (value: unknown): number | null => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return null;
  }

  const raw = String(value).trim();
  const direct = Number(raw);
  if (Number.isFinite(direct)) return direct;

  // Also accept values such as "10 Hours" or "10-hour shift".
  const match = raw.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const extractApiRecords = (response: any): any[] => {
  const candidates = [
    response?.data?.data,
    response?.data?.result,
    response?.data?.Result,
    response?.data?.items,
    response?.data?.Items,
    response?.data?.records,
    response?.data?.Records,
    response?.data,
    response?.result,
    response?.Result,
    response?.items,
    response?.Items,
    response?.records,
    response?.Records,
    response,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const resolveEmployeeType = (value: unknown): EmployeeType => {
  const normalized = normalizeText(value);

  // ShiftMaster values include "Payroll CNC", "Contract CNC", and
  // "CNC Apprentice". Check the explicit category before the generic
  // CNC keyword so "Payroll CNC" is classified as Payroll.
  if (normalized.includes("contract")) return "contract";
  if (normalized.includes("apprentice")) return "cnc";
  if (normalized.includes("payroll")) return "payroll";
  if (normalized.includes("cnc")) return "cnc";

  // Preserve the existing fallback for missing/unrecognized values.
  return "payroll";
};

const employeeTypeLabel = (value: EmployeeType) =>
  EMPLOYEE_TYPE_OPTIONS.find((item) => item.value === value)?.label ??
  "Payroll";

const shortName = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "EM";

const getMonthYearLabel = (month: number, year: number) =>
  `${MONTH_OPTIONS[month - 1]} ${year}`;

// Shared sizing for every dropdown field on this page.
// Keep the control height fixed while vertically centering the selected value.
const fieldSx = {
  minWidth: 0,
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    height: "40px !important",
    minHeight: "40px !important",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    height: "40px !important",
    minHeight: "40px !important",
    maxHeight: "40px",
    py: "0 !important",
    pr: "32px !important",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  "& .MuiInputLabel-root": { fontSize: 14.5 },
};

const dropdownMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      "& .MuiMenuItem-root": {
        minHeight: 40,
        boxSizing: "border-box",
      },
    },
  },
};

const panelHeaderSx = {
  px: 2,
  py: 1.5,
  backgroundColor: "#E5FAFB",
  borderBottom: "1px solid #d5eff0",
};

type RosterChangeAction = "MOVE" | "SWITCH" | "MERGE" | "REMOVE";

export interface ShiftRosterChangePayload {
  /** Department being managed. Backend must authorize this using the logged-in user's department. */
  departmentId: string;
  subDepartmentId: string;
  employeeType: EmployeeType;
  shiftHours: ShiftHours;
  scheduleMonth: number;
  scheduleYear: number;
  changes: Array<{
    personId: string;
    // Required by SaveShiftRosterChanges: this must be the employee's real
    // source shift for the selected period, not the destination shift.
    fromShiftMasterId: string;
    toShiftMasterId: string | null;
    removed: boolean;
    actionType: RosterChangeAction;
  }>;
}

interface ShiftRosterManagementPageProps {
  /** Connect this callback to the roster-save API when the backend is ready. */
  onSubmitChanges?: (payload: ShiftRosterChangePayload) => Promise<void>;
}

export default function ShiftRosterManagementPage({
  onSubmitChanges,
}: ShiftRosterManagementPageProps = {}) {
  const [selectedSubDepartmentId, setSelectedSubDepartmentId] = useState("");
  const [employeeType, setEmployeeType] = useState<EmployeeType>("payroll");
  const [shiftHours, setShiftHours] = useState<ShiftHours>(10);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [draftActionTypes, setDraftActionTypes] = useState<
    Record<string, RosterChangeAction>
  >({});
  // currentDepartment is the authenticated user's own department.
  // selectedDepartmentId is the scope currently displayed in the planner.
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [currentDepartment, setCurrentDepartment] =
    useState<DepartmentOption | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [isHrUser, setIsHrUser] = useState(false);
  const [departmentBootstrapLoading, setDepartmentBootstrapLoading] =
    useState(true);
  const [subDepartments, setSubDepartments] = useState<SubDepartmentOption[]>(
    [],
  );
  const [allShifts, setAllShifts] = useState<ShiftOption[]>([]);
  const [employees, setEmployees] = useState<RosterEmployee[]>([]);
  const [employeeDirectory, setEmployeeDirectory] = useState<
    EmployeeDropdownOption[]
  >([]);
  const [employeeDirectoryLoading, setEmployeeDirectoryLoading] =
    useState(false);
  const [originalAssignments, setOriginalAssignments] = useState<
    Record<string, string>
  >({});
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [removedEmployeeKeys, setRemovedEmployeeKeys] = useState<string[]>([]);

  // null means the page has not initialized the default pair yet. [] is a user selection.
  const [selectedShiftIdsState, setSelectedShiftIdsState] = useState<
    string[] | null
  >(null);
  const selectedShiftIds = selectedShiftIdsState ?? [];
  const [leftShiftId, setLeftShiftId] = useState("");
  const [rightShiftId, setRightShiftId] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [leftPage, setLeftPage] = useState(0);
  const [rightPage, setRightPage] = useState(0);
  // Each shift card can be resized independently. The fit height is measured
  // from the actual employee cards, so resizing stops when all visible-page rows fit.
  const [leftCardHeight, setLeftCardHeight] = useState(620);
  const [rightCardHeight, setRightCardHeight] = useState(620);
  const [leftCardFitHeight, setLeftCardFitHeight] = useState(1600);
  const [rightCardFitHeight, setRightCardFitHeight] = useState(1600);
  const leftCardRef = useRef<HTMLDivElement | null>(null);
  const rightCardRef = useRef<HTMLDivElement | null>(null);
  const leftEmployeeListRef = useRef<HTMLDivElement | null>(null);
  const rightEmployeeListRef = useRef<HTMLDivElement | null>(null);
  const [resizeDrag, setResizeDrag] = useState<{
    side: "left" | "right";
    startY: number;
    startHeight: number;
  } | null>(null);
  const [panelRowsPerPage, setPanelRowsPerPage] = useState(5);
  const [leftAddEmployeeKey, setLeftAddEmployeeKey] = useState("");
  const [rightAddEmployeeKey, setRightAddEmployeeKey] = useState("");
  const [leftAddEmployeeSearch, setLeftAddEmployeeSearch] = useState("");
  const [rightAddEmployeeSearch, setRightAddEmployeeSearch] = useState("");
  const [leftAddDropdownOpen, setLeftAddDropdownOpen] = useState(false);
  const [rightAddDropdownOpen, setRightAddDropdownOpen] = useState(false);
  const [draggedEmployeeKey, setDraggedEmployeeKey] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTargetShiftId, setMergeTargetShiftId] = useState("");
  const [mergeSourceShiftIds, setMergeSourceShiftIds] = useState<string[]>([]);
  const [finalSubmitting, setFinalSubmitting] = useState(false);

  // Always show shifts by their actual Shift number, not by the API response
  // order (which can vary between departments and employee categories).
  const availableShifts = useMemo(() => {
    const getShiftNumber = (label: string): number | null => {
      const match = label.match(/\\bshift\\s*(\\d+)\\b/i);
      return match ? Number(match[1]) : null;
    };

    return allShifts
      .filter((shift) => shift.employeeType === employeeType)
      .sort((a, b) => {
        const aNumber = getShiftNumber(a.label);
        const bNumber = getShiftNumber(b.label);

        if (aNumber !== null && bNumber !== null) return aNumber - bNumber;
        if (aNumber !== null) return -1;
        if (bNumber !== null) return 1;

        return a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
  }, [allShifts, employeeType]);

  const selectedSubDepartment = useMemo(
    () => subDepartments.find((item) => item.id === selectedSubDepartmentId),
    [subDepartments, selectedSubDepartmentId],
  );

  const leftShift = useMemo(
    () => availableShifts.find((shift) => shift.id === leftShiftId),
    [availableShifts, leftShiftId],
  );
  const rightShift = useMemo(
    () => availableShifts.find((shift) => shift.id === rightShiftId),
    [availableShifts, rightShiftId],
  );

  useEffect(() => {
    let cancelled = false;

    const loadDepartmentScope = async () => {
      setDepartmentBootstrapLoading(true);
      try {
        const [departmentResponse, userResponse] = await Promise.all([
          GetAllDepartments(),
          GetUserDetail(),
        ]);
        if (cancelled) return;

        const allDepartmentOptions =
          normalizeDepartmentOptions(departmentResponse);
        const userDetail =
          userResponse?.data?.data ??
          userResponse?.data ??
          userResponse?.Data ??
          userResponse ??
          {};

        const departmentIdFromUser = String(
          getApiField(
            userDetail,
            "departmentId",
            "DepartmentId",
            "empDeptId",
            "EmpDeptId",
            "deptId",
            "DeptId",
          ) ??
            (String(getApiField(userDetail, "empDept", "EmpDept") ?? "")
              .trim()
              .match(/^\d+$/)
              ? getApiField(userDetail, "empDept", "EmpDept")
              : "") ??
            "",
        ).trim();

        const departmentNameFromUser = String(
          getApiField(
            userDetail,
            "departmentName",
            "DepartmentName",
            "department",
            "Department",
            "empDeptName",
            "EmpDeptName",
            "deptName",
            "DeptName",
          ) ??
            (String(getApiField(userDetail, "empDept", "EmpDept") ?? "")
              .trim()
              .match(/^\d+$/)
              ? ""
              : getApiField(userDetail, "empDept", "EmpDept")) ??
            "",
        ).trim();

        let ownDepartment =
          allDepartmentOptions.find(
            (department) =>
              departmentIdFromUser && department.id === departmentIdFromUser,
          ) ??
          allDepartmentOptions.find(
            (department) =>
              departmentNameFromUser &&
              normalizeText(department.label) ===
                normalizeText(departmentNameFromUser),
          ) ??
          null;

        if (
          !ownDepartment &&
          (departmentIdFromUser || departmentNameFromUser)
        ) {
          ownDepartment = {
            id:
              departmentIdFromUser ||
              `department:${normalizeText(departmentNameFromUser)}`,
            label:
              departmentNameFromUser || `Department ID ${departmentIdFromUser}`,
          };
          allDepartmentOptions.push(ownDepartment);
          allDepartmentOptions.sort((a, b) => a.label.localeCompare(b.label));
        }

        setDepartments(allDepartmentOptions);
        setCurrentDepartment(ownDepartment);
        setSelectedDepartmentId(ownDepartment?.id ?? "");
        setIsHrUser(isHrDepartment(ownDepartment?.label));
        if (!ownDepartment) {
          setLoadError(
            "Unable to determine the logged-in user's department from GetUserDetail().",
          );
        }
      } catch (error) {
        console.error("Failed to load department list or user details:", error);
        if (!cancelled) {
          setDepartments([]);
          setCurrentDepartment(null);
          setSelectedDepartmentId("");
          setIsHrUser(false);
          setLoadError(
            "Unable to load departments or determine the logged-in user's department.",
          );
        }
      } finally {
        if (!cancelled) setDepartmentBootstrapLoading(false);
      }
    };

    void loadDepartmentScope();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the complete employee master list separately from the monthly roster.
  // The Add / replace dropdown must also include employees without a current shift.
  useEffect(() => {
    let cancelled = false;

    const loadEmployeeDirectory = async () => {
      setEmployeeDirectoryLoading(true);
      try {
        const response = await GetAllEmployeeDetailsDropdowns(true);
        // This endpoint returns its employee list in data.buddyDropDown.
        // Fall back to the common API response shapes used elsewhere in the app.
        const records: any[] = Array.isArray(response?.data?.buddyDropDown)
          ? response.data.buddyDropDown
          : Array.isArray(response?.buddyDropDown)
          ? response.buddyDropDown
          : extractApiRecords(response);
        const uniqueEmployees = new Map<string, EmployeeDropdownOption>();

        records.forEach((item: any) => {
          const personId = String(
            getApiField(
              item,
              "personId",
              "PersonId",
              "Person_ID",
              "person_ID",
              "employeeId",
              "EmployeeId",
              "employeeCode",
              "EmployeeCode",
              "employeeNumber",
              "EmployeeNumber",
              "empId",
              "EmpId",
              "empCode",
              "EmpCode",
              "id",
              "Id",
              "value",
              "Value",
            ) ?? "",
          ).trim();
          const personName = String(
            getApiField(
              item,
              "personName",
              "PersonName",
              "Person_Name",
              "employeeName",
              "EmployeeName",
              "buddyName",
              "BuddyName",
              "fullName",
              "FullName",
              "empName",
              "EmpName",
              "name",
              "Name",
              "label",
              "Label",
              "text",
              "Text",
            ) ?? "",
          ).trim();

          if (!personId) return;
          const key = normalizeText(personId);
          if (uniqueEmployees.has(key)) return;

          uniqueEmployees.set(key, {
            personId,
            personName: personName || `Employee ${personId}`,
            offDay: String(
              getApiField(
                item,
                "offDay",
                "OffDay",
                "Off_day",
                "weeklyOff",
                "WeeklyOff",
              ) ?? "",
            ).trim(),
          });
        });

        if (!cancelled) {
          const options = Array.from(uniqueEmployees.values()).sort((a, b) =>
            a.personName.localeCompare(b.personName, undefined, {
              sensitivity: "base",
            }),
          );
          setEmployeeDirectory(options);
          if (options.length === 0) {
            console.warn(
              "[ShiftRosterManagement] GetAllEmployeeDetailsDropdowns returned no usable employee IDs.",
              response,
            );
          }
        }
      } catch (error) {
        console.error(
          "Failed to load the complete employee dropdown list:",
          error,
        );
        if (!cancelled) {
          setEmployeeDirectory([]);
          toast.error(
            "Unable to load the employee list. Please refresh and try again.",
          );
        }
      } finally {
        if (!cancelled) setEmployeeDirectoryLoading(false);
      }
    };

    void loadEmployeeDirectory();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedDepartmentId) return;

    setLoading(true);
    setLoadError("");

    try {
      // Frontend service wrappers pass the selected department in the query/body.
      // The backend must later authorize HR cross-department access and honor it.
      const [subDepartmentResponse, shiftResponse, recordsResponse] =
        await Promise.all([
          GetASAISubDepartments(selectedDepartmentId),
          GetDepartmentShifts(shiftHours, selectedDepartmentId),
          GetCurrentShiftImportRecords({
            scheduleMonth: selectedMonth,
            scheduleYear: selectedYear,
            departmentId: Number(selectedDepartmentId),
            shiftHours,
          } as any),
        ]);

      const subDepartmentRecords = extractApiRecords(subDepartmentResponse);
      const normalizedSubDepartments: SubDepartmentOption[] =
        subDepartmentRecords
          .map((item: any): SubDepartmentOption | null => {
            const id = String(
              getApiField(
                item,
                "id",
                "Id",
                "subDepartmentId",
                "SubDepartmentId",
              ) ?? "",
            ).trim();
            const label = String(
              getApiField(
                item,
                "machineName",
                "MachineName",
                "subDepartmentName",
                "SubDepartmentName",
              ) ?? "",
            ).trim();
            const code = String(
              getApiField(
                item,
                "machineCode",
                "MachineCode",
                "subDepartmentCode",
                "SubDepartmentCode",
              ) ?? "",
            ).trim();
            return id && label ? { id, label, code } : null;
          })
          .filter((item): item is SubDepartmentOption => item !== null);

      const shiftRecords = extractApiRecords(shiftResponse);
      const normalizedShifts: ShiftOption[] = shiftRecords
        .map((item: any): ShiftOption | null => {
          const id = String(
            getApiField(
              item,
              "id",
              "Id",
              "shiftMasterId",
              "ShiftMasterId",
              "shiftMasterID",
              "ShiftMasterID",
              "shiftId",
              "ShiftId",
              "shiftID",
              "ShiftID",
            ) ?? "",
          ).trim();
          // ShiftMaster responses can omit ShiftType. Match the existing import
          // page behavior: default an unrecognized type to Payroll.
          const shiftType = String(
            getApiField(
              item,
              "shiftType",
              "ShiftType",
              "employeeType",
              "EmployeeType",
              "employeeCategory",
              "EmployeeCategory",
            ) ?? "",
          ).trim();
          const rawHours = getApiField(
            item,
            "shiftHours",
            "ShiftHours",
            "shiftDurationHours",
            "ShiftDurationHours",
            "hours",
            "Hours",
          );
          const parsedHours = parseShiftHours(rawHours);
          const rawName = String(
            getApiField(
              item,
              "shiftName",
              "ShiftName",
              "displayName",
              "DisplayName",
            ) ?? "",
          ).trim();
          const rawNumber = String(
            getApiField(
              item,
              "shiftNumber",
              "ShiftNumber",
              "shiftNo",
              "ShiftNo",
            ) ?? "",
          ).trim();
          if (!id || (parsedHours !== null && parsedHours !== shiftHours))
            return null;
          return {
            id,
            shiftType,
            // As on Import/View Shift, missing or unrecognized type defaults to Payroll.
            employeeType: resolveEmployeeType(shiftType),
            shiftHours,
            label:
              rawName ||
              `${shiftType || `${shiftHours}-hour`} — Shift ${rawNumber || id}`,
          };
        })
        .filter((item): item is ShiftOption => item !== null);

      // Keep a useful diagnostic while wiring this module to the existing API.
      // If the API has returned data but the list is empty, this exposes the raw DTO
      // shape in DevTools instead of hiding the cause behind an empty dropdown.
      if (shiftRecords.length === 0 || normalizedShifts.length === 0) {
        console.warn("[ShiftRosterManagement] No usable department shifts", {
          requestedShiftHours: shiftHours,
          returnedRecordCount: shiftRecords.length,
          normalizedShiftCount: normalizedShifts.length,
          firstShiftRecord: shiftRecords[0] ?? null,
          response: shiftResponse,
        });
      }

      const rawEmployees = extractApiRecords(recordsResponse);

      const employeeMap = new Map<string, RosterEmployee>();
      rawEmployees.forEach((item: any) => {
        const shiftId = String(
          getApiField(
            item,
            "shiftMasterId",
            "ShiftMasterId",
            "shiftMasterID",
            "ShiftMasterID",
            "shiftId",
            "ShiftId",
            "shiftID",
            "ShiftID",
          ) ?? "",
        ).trim();
        const shift = normalizedShifts.find((option) => option.id === shiftId);
        if (!shift) return;

        const personId = String(
          getApiField(item, "personId", "PersonId", "Person_ID", "person_ID") ??
            "",
        ).trim();
        const personName = String(
          getApiField(
            item,
            "personName",
            "PersonName",
            "Person_Name",
            "name",
            "Name",
          ) ?? "",
        ).trim();
        if (!personId) return;

        const rawSubDepartmentId = String(
          getApiField(item, "subDepartmentId", "SubDepartmentId") ?? "",
        ).trim();
        const subDepartmentName = String(
          getApiField(
            item,
            "subDepartmentName",
            "SubDepartmentName",
            "subDepartment",
            "SubDepartment",
            "machineName",
            "MachineName",
          ) ?? "",
        ).trim();
        const matchingSubDepartment = normalizedSubDepartments.find(
          (subDepartment) =>
            (rawSubDepartmentId && subDepartment.id === rawSubDepartmentId) ||
            (!rawSubDepartmentId &&
              normalizeText(subDepartment.label) ===
                normalizeText(subDepartmentName)),
        );
        const subDepartmentId =
          rawSubDepartmentId ||
          matchingSubDepartment?.id ||
          (subDepartmentName ? `name:${normalizeText(subDepartmentName)}` : "");
        if (!subDepartmentId) return;

        const key = `${shiftId}::${subDepartmentId}::${shift.employeeType}::${personId}`;
        employeeMap.set(key, {
          key,
          personId,
          personName: personName || "Unnamed employee",
          offDay: String(
            getApiField(item, "offDay", "OffDay", "Off_day") ?? "",
          ).trim(),
          subDepartmentId,
          subDepartmentName:
            subDepartmentName ||
            matchingSubDepartment?.label ||
            "Sub-department",
          employeeType: shift.employeeType,
          originalShiftId: shiftId,
        });
      });

      const normalizedEmployees = Array.from(employeeMap.values());
      const nextAssignments = Object.fromEntries(
        normalizedEmployees.map((employee) => [
          employee.key,
          employee.originalShiftId,
        ]),
      );

      setSubDepartments(() => {
        const byId = new Map<string, SubDepartmentOption>();
        normalizedSubDepartments.forEach((item) => byId.set(item.id, item));
        normalizedEmployees.forEach((employee) => {
          if (!byId.has(employee.subDepartmentId)) {
            byId.set(employee.subDepartmentId, {
              id: employee.subDepartmentId,
              label: employee.subDepartmentName,
              code: "",
            });
          }
        });
        return Array.from(byId.values()).sort((a, b) =>
          a.label.localeCompare(b.label),
        );
      });
      setAllShifts(normalizedShifts);
      setEmployees(normalizedEmployees);
      setOriginalAssignments(nextAssignments);
      setAssignments(nextAssignments);
      setDraftActionTypes({});
      setRemovedEmployeeKeys([]);
      setLeftAddEmployeeKey("");
      setRightAddEmployeeKey("");
      setLeftSearch("");
      setRightSearch("");
      setLeftPage(0);
      setRightPage(0);
    } catch (error) {
      console.error("Failed to load shift roster:", error);
      setLoadError(
        "Unable to load shifts and employee records for the selected department. Verify the department-scoped ASAI APIs.",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedDepartmentId, selectedMonth, selectedYear, shiftHours]);

  useEffect(() => {
    if (selectedDepartmentId) void loadData();
  }, [loadData, selectedDepartmentId]);

  useEffect(() => {
    if (!subDepartments.length) {
      if (selectedSubDepartmentId) setSelectedSubDepartmentId("");
      return;
    }
    if (!subDepartments.some((item) => item.id === selectedSubDepartmentId)) {
      setSelectedSubDepartmentId(subDepartments[0].id);
    }
  }, [subDepartments, selectedSubDepartmentId]);

  // Initialize or repair the selected shift pair whenever shift hours/type changes.
  useEffect(() => {
    if (!availableShifts.length) {
      if (selectedShiftIdsState !== null && selectedShiftIds.length)
        setSelectedShiftIdsState([]);
      setLeftShiftId("");
      setRightShiftId("");
      return;
    }

    const validIds = new Set(availableShifts.map((shift) => shift.id));
    if (selectedShiftIdsState === null) {
      const defaults = availableShifts
        .slice(0, Math.min(2, availableShifts.length))
        .map((shift) => shift.id);
      setSelectedShiftIdsState(defaults);
      setLeftShiftId(defaults[0] ?? "");
      setRightShiftId(defaults[1] ?? "");
      return;
    }

    const kept = selectedShiftIds.filter((id) => validIds.has(id));
    if (kept.length !== selectedShiftIds.length) {
      const repaired = kept.length
        ? kept
        : availableShifts
            .slice(0, Math.min(2, availableShifts.length))
            .map((shift) => shift.id);
      setSelectedShiftIdsState(repaired);
      setLeftShiftId(repaired[0] ?? "");
      setRightShiftId(repaired.find((id) => id !== repaired[0]) ?? "");
      return;
    }

    if (leftShiftId && !kept.includes(leftShiftId))
      setLeftShiftId(kept[0] ?? "");
    if (
      rightShiftId &&
      (!kept.includes(rightShiftId) || rightShiftId === leftShiftId)
    ) {
      setRightShiftId(
        kept.find(
          (id) =>
            id !==
            (leftShiftId && kept.includes(leftShiftId) ? leftShiftId : kept[0]),
        ) ?? "",
      );
    }
  }, [
    availableShifts,
    selectedShiftIdsState,
    selectedShiftIds,
    leftShiftId,
    rightShiftId,
  ]);

  const scopedEmployees = useMemo(
    () =>
      employees.filter(
        (employee) =>
          employee.subDepartmentId === selectedSubDepartmentId &&
          employee.employeeType === employeeType,
      ),
    [employees, selectedSubDepartmentId, employeeType],
  );

  const removedSet = useMemo(
    () => new Set(removedEmployeeKeys),
    [removedEmployeeKeys],
  );

  const getShiftEmployees = useCallback(
    (shiftId: string, search = "") => {
      const query = normalizeText(search);
      return scopedEmployees
        .filter((employee) => {
          if (
            removedSet.has(employee.key) ||
            assignments[employee.key] !== shiftId
          )
            return false;
          if (!query) return true;
          return normalizeText(
            `${employee.personId} ${employee.personName} ${employee.offDay}`,
          ).includes(query);
        })
        .sort((a, b) =>
          a.personName.localeCompare(b.personName, undefined, {
            sensitivity: "base",
          }),
        );
    },
    [scopedEmployees, removedSet, assignments],
  );

  const leftEmployees = leftShift
    ? getShiftEmployees(leftShift.id, leftSearch)
    : [];
  const rightEmployees = rightShift
    ? getShiftEmployees(rightShift.id, rightSearch)
    : [];

  const leftVisiblePageRows = leftEmployees.slice(
    leftPage * panelRowsPerPage,
    leftPage * panelRowsPerPage + panelRowsPerPage,
  );
  const rightVisiblePageRows = rightEmployees.slice(
    rightPage * panelRowsPerPage,
    rightPage * panelRowsPerPage + panelRowsPerPage,
  );

  useEffect(() => {
    let disposed = false;
    let scheduledFrame: number | null = null;

    const leftList = leftEmployeeListRef.current;
    const rightList = rightEmployeeListRef.current;

    const measureFitHeight = (
      card: HTMLDivElement | null,
      list: HTMLDivElement | null,
      setFitHeight: React.Dispatch<React.SetStateAction<number>>,
    ) => {
      if (!card || !list) return;

      const employeeCards = Array.from(
        list.querySelectorAll<HTMLElement>(
          '[data-roster-employee-card="true"]',
        ),
      );

      // Empty states retain a compact drop target; populated lists fit to their actual rows.
      if (employeeCards.length === 0) {
        setFitHeight((previous) => (previous === 420 ? previous : 420));
        return;
      }

      const listStyles = window.getComputedStyle(list);
      const rowGap = Number.parseFloat(listStyles.rowGap || "0") || 0;
      const paddingTop = Number.parseFloat(listStyles.paddingTop || "0") || 0;
      const paddingBottom =
        Number.parseFloat(listStyles.paddingBottom || "0") || 0;
      const borderTop =
        Number.parseFloat(listStyles.borderTopWidth || "0") || 0;
      const borderBottom =
        Number.parseFloat(listStyles.borderBottomWidth || "0") || 0;

      const rowsHeight = employeeCards.reduce(
        (total, row) => total + row.getBoundingClientRect().height,
        0,
      );
      const listContentHeight =
        rowsHeight +
        Math.max(0, employeeCards.length - 1) * rowGap +
        paddingTop +
        paddingBottom +
        borderTop +
        borderBottom;

      // Everything outside the scrolling list is fixed-height panel chrome.
      const panelChromeHeight =
        card.getBoundingClientRect().height -
        list.getBoundingClientRect().height;
      const nextFitHeight = Math.max(
        1,
        Math.ceil(panelChromeHeight + listContentHeight),
      );

      setFitHeight((previous) =>
        Math.abs(previous - nextFitHeight) > 1 ? nextFitHeight : previous,
      );
    };

    const measureBoth = () => {
      measureFitHeight(leftCardRef.current, leftList, setLeftCardFitHeight);
      measureFitHeight(rightCardRef.current, rightList, setRightCardFitHeight);
    };

    // Run measurements outside the ResizeObserver delivery cycle. Updating
    // card height synchronously from an observer callback can cause the browser
    // to report: "ResizeObserver loop completed with undelivered notifications".
    const scheduleMeasure = () => {
      if (disposed || scheduledFrame !== null) return;
      scheduledFrame = window.requestAnimationFrame(() => {
        scheduledFrame = null;
        if (!disposed) measureBoth();
      });
    };

    scheduleMeasure();

    if (typeof ResizeObserver === "undefined") {
      return () => {
        disposed = true;
        if (scheduledFrame !== null) {
          window.cancelAnimationFrame(scheduledFrame);
        }
      };
    }

    // Watch list WIDTH only (not its height, which changes when the card is
    // fitted) and the employee rows themselves. Do not observe the card: its
    // height is the state being updated by this measurement.
    const listWidths = new WeakMap<Element, number>();
    [leftList, rightList].forEach((list) => {
      if (list) listWidths.set(list, list.getBoundingClientRect().width);
    });

    const observer = new ResizeObserver((entries) => {
      let shouldMeasure = false;

      entries.forEach((entry) => {
        const target = entry.target;
        if (target === leftList || target === rightList) {
          const previousWidth = listWidths.get(target);
          const nextWidth = entry.contentRect.width;
          listWidths.set(target, nextWidth);
          if (
            previousWidth === undefined ||
            Math.abs(previousWidth - nextWidth) > 1
          ) {
            shouldMeasure = true;
          }
          return;
        }

        if (
          target instanceof HTMLElement &&
          target.dataset.rosterEmployeeCard === "true"
        ) {
          shouldMeasure = true;
        }
      });

      if (shouldMeasure) scheduleMeasure();
    });

    [leftList, rightList].forEach((list) => {
      if (!list) return;
      observer.observe(list);
      list
        .querySelectorAll<HTMLElement>('[data-roster-employee-card="true"]')
        .forEach((employeeCard) => observer.observe(employeeCard));
    });

    return () => {
      disposed = true;
      observer.disconnect();
      if (scheduledFrame !== null) {
        window.cancelAnimationFrame(scheduledFrame);
      }
    };
  }, [
    leftVisiblePageRows.map((row) => row.key).join("|"),
    rightVisiblePageRows.map((row) => row.key).join("|"),
    leftEmployees.length,
    rightEmployees.length,
    leftPage,
    rightPage,
    panelRowsPerPage,
    leftShiftId,
    rightShiftId,
    selectedSubDepartmentId,
    employeeType,
    shiftHours,
    leftSearch,
    rightSearch,
    loading,
  ]);

  const hasDraftChanges = useMemo(() => {
    const removedChanged = removedEmployeeKeys.length > 0;
    const assignmentChanged = Object.keys(originalAssignments).some(
      (key) => (originalAssignments[key] ?? "") !== (assignments[key] ?? ""),
    );
    return removedChanged || assignmentChanged;
  }, [originalAssignments, assignments, removedEmployeeKeys]);

  const changedEmployees = useMemo(
    () =>
      scopedEmployees.filter(
        (employee) =>
          (originalAssignments[employee.key] ?? "") !==
            (assignments[employee.key] ?? "") || removedSet.has(employee.key),
      ),
    [scopedEmployees, originalAssignments, assignments, removedSet],
  );

  const changeShiftSelection = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    const next = (
      typeof rawValue === "string" ? rawValue.split(",") : rawValue
    ).filter(Boolean);
    setSelectedShiftIdsState(next);
    setLeftShiftId((current) =>
      next.includes(current) ? current : next[0] ?? "",
    );
    setRightShiftId((current) =>
      next.includes(current) && current !== (next[0] ?? "")
        ? current
        : next.find((id) => id !== (next[0] ?? "")) ?? "",
    );
  };

  const markDraftChanged = () => {
    // State changes are deliberately local until the save endpoint is designed.
  };

  const moveEmployee = (employeeKey: string, targetShiftId: string) => {
    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }
    if (
      !targetShiftId ||
      !scopedEmployees.some((employee) => employee.key === employeeKey)
    )
      return;
    setAssignments((previous) => ({
      ...previous,
      [employeeKey]: targetShiftId,
    }));
    setRemovedEmployeeKeys((previous) =>
      previous.filter((key) => key !== employeeKey),
    );
    setDraftActionTypes((previous) => ({
      ...previous,
      [employeeKey]: "MOVE",
    }));
    setDraggedEmployeeKey("");
    markDraftChanged();
  };

  const removeEmployee = (employeeKey: string) => {
    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }

    // Removing a newly added draft employee cancels that local addition; do not
    // send a REMOVE operation for someone who never had a saved roster record.
    if (employeeKey.startsWith("new::")) {
      setEmployees((previous) =>
        previous.filter((employee) => employee.key !== employeeKey),
      );
      setOriginalAssignments((previous) => {
        const next = { ...previous };
        delete next[employeeKey];
        return next;
      });
      setAssignments((previous) => {
        const next = { ...previous };
        delete next[employeeKey];
        return next;
      });
      setRemovedEmployeeKeys((previous) =>
        previous.filter((key) => key !== employeeKey),
      );
      setDraftActionTypes((previous) => {
        const next = { ...previous };
        delete next[employeeKey];
        return next;
      });
      return;
    }

    setAssignments((previous) => ({ ...previous, [employeeKey]: "" }));
    setRemovedEmployeeKeys((previous) =>
      previous.includes(employeeKey) ? previous : [...previous, employeeKey],
    );
    setDraftActionTypes((previous) => ({
      ...previous,
      [employeeKey]: "REMOVE",
    }));
  };

  const swapAllEmployees = () => {
    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }
    if (!leftShift || !rightShift || leftShift.id === rightShift.id) return;
    const scopedKeys = new Set(scopedEmployees.map((employee) => employee.key));
    const switchedKeys = scopedEmployees
      .filter(
        (employee) =>
          !removedSet.has(employee.key) &&
          (assignments[employee.key] === leftShift.id ||
            assignments[employee.key] === rightShift.id),
      )
      .map((employee) => employee.key);
    setAssignments((previous) => {
      const next = { ...previous };
      Object.keys(next).forEach((key) => {
        if (!scopedKeys.has(key) || removedSet.has(key)) return;
        if (next[key] === leftShift.id) next[key] = rightShift.id;
        else if (next[key] === rightShift.id) next[key] = leftShift.id;
      });
      return next;
    });
    setDraftActionTypes((previous) => ({
      ...previous,
      ...Object.fromEntries(
        switchedKeys.map((key) => [key, "SWITCH" as RosterChangeAction]),
      ),
    }));
  };

  const openMergeDialog = () => {
    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }
    if (selectedShiftIds.length < 2) {
      toast.info("Select at least two shifts before merging.");
      return;
    }
    if (!selectedSubDepartmentId) {
      toast.info("Select a sub-department before merging shifts.");
      return;
    }

    const defaultTarget = selectedShiftIds.includes(leftShiftId)
      ? leftShiftId
      : selectedShiftIds[0];
    setMergeTargetShiftId(defaultTarget);
    setMergeSourceShiftIds(
      selectedShiftIds.filter((id) => id !== defaultTarget),
    );
    setMergeDialogOpen(true);
  };

  const handleMergeShifts = () => {
    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }

    const sourceIds = new Set(
      mergeSourceShiftIds.filter(
        (id) => id !== mergeTargetShiftId && selectedShiftIds.includes(id),
      ),
    );
    if (!mergeTargetShiftId || sourceIds.size === 0) {
      toast.info("Choose a target shift and at least one source shift.");
      return;
    }

    // This is an assignment merge, not a request to create duplicate assignments.
    // The database has one Current assignment per PersonId + target shift + month/year.
    const movingEmployees = scopedEmployees.filter(
      (employee) =>
        !removedSet.has(employee.key) &&
        sourceIds.has(assignments[employee.key] ?? ""),
    );

    if (movingEmployees.length === 0) {
      toast.info(
        "There are no employees in the selected source shifts to merge.",
      );
      return;
    }

    // A person already assigned to the target must not appear a second time in
    // the target panel. Also de-duplicate repeated PersonIds across source shifts.
    const targetPersonIds = new Set(
      scopedEmployees
        .filter(
          (employee) =>
            !removedSet.has(employee.key) &&
            assignments[employee.key] === mergeTargetShiftId,
        )
        .map((employee) => normalizeText(employee.personId))
        .filter(Boolean),
    );

    const moveToTargetKeys: string[] = [];
    const sourceDuplicateKeys: string[] = [];

    movingEmployees.forEach((employee) => {
      const personKey = normalizeText(employee.personId);
      if (!personKey || targetPersonIds.has(personKey)) {
        // The target already contains this person. Archive/consolidate the
        // source assignment on submit, but do not display a duplicate target row.
        sourceDuplicateKeys.push(employee.key);
        return;
      }

      targetPersonIds.add(personKey);
      moveToTargetKeys.push(employee.key);
    });

    const movingKeys = new Set(movingEmployees.map((employee) => employee.key));
    const newTargetKeys = new Set(moveToTargetKeys);
    const duplicateKeys = new Set(sourceDuplicateKeys);

    setAssignments((previous) => {
      const next = { ...previous };
      newTargetKeys.forEach((key) => {
        next[key] = mergeTargetShiftId;
      });
      duplicateKeys.forEach((key) => {
        next[key] = mergeTargetShiftId;
      });
      return next;
    });

    setRemovedEmployeeKeys((previous) => {
      const retained = previous.filter((key) => !movingKeys.has(key));
      return Array.from(new Set([...retained, ...sourceDuplicateKeys]));
    });

    setDraftActionTypes((previous) => ({
      ...previous,
      ...Object.fromEntries(
        Array.from(movingKeys).map((key) => [
          key,
          "MERGE" as RosterChangeAction,
        ]),
      ),
    }));

    const firstSourceId = Array.from(sourceIds)[0];
    setLeftShiftId(mergeTargetShiftId);
    setRightShiftId(firstSourceId ?? "");
    setLeftPage(0);
    setRightPage(0);
    setMergeDialogOpen(false);

    const targetLabel =
      availableShifts.find((shift) => shift.id === mergeTargetShiftId)?.label ??
      "the selected target shift";

    if (moveToTargetKeys.length === 0) {
      toast.success(
        `${sourceDuplicateKeys.length} source assignment(s) already existed in ${targetLabel}. Duplicate rows will be consolidated; no new unique employees will be added. Review changes to submit.`,
      );
    } else {
      toast.success(
        `${moveToTargetKeys.length} new unique employee(s) will be added to ${targetLabel}; ${sourceDuplicateKeys.length} duplicate source assignment(s) will be consolidated. Review changes to submit the draft.`,
      );
    }
  };

  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    targetShiftId: string,
  ) => {
    event.preventDefault();
    const employeeKey =
      event.dataTransfer.getData("text/plain") || draggedEmployeeKey;
    if (employeeKey) moveEmployee(employeeKey, targetShiftId);
  };

  const addEmployeeToShift = (
    targetShiftId: string,
    personId: string,
    side: "left" | "right",
  ) => {
    if (!personId || !targetShiftId || !selectedSubDepartmentId) return;

    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }

    const selectedEmployee = employeeDirectory.find(
      (item) => normalizeText(item.personId) === normalizeText(personId),
    );
    if (!selectedEmployee) {
      toast.error(
        "The selected employee could not be found. Refresh the list and try again.",
      );
      return;
    }

    const normalizedPersonId = normalizeText(selectedEmployee.personId);
    const existingInCurrentScope = scopedEmployees.find(
      (employee) => normalizeText(employee.personId) === normalizedPersonId,
    );

    // If the employee is already in this sub-department and employee category,
    // keep the existing roster identity and move them to the selected shift.
    if (existingInCurrentScope) {
      moveEmployee(existingInCurrentScope.key, targetShiftId);
    } else {
      // Do not silently create a second assignment if the person already appears
      // in a different sub-department/category in the loaded department roster.
      const existingOutsideScope = employees.find(
        (employee) =>
          normalizeText(employee.personId) === normalizedPersonId &&
          (employee.subDepartmentId !== selectedSubDepartmentId ||
            employee.employeeType !== employeeType) &&
          !removedSet.has(employee.key) &&
          Boolean(assignments[employee.key]),
      );

      if (existingOutsideScope) {
        toast.error(
          `${selectedEmployee.personName} is already assigned under ${
            existingOutsideScope.subDepartmentName
          } (${employeeTypeLabel(
            existingOutsideScope.employeeType,
          )}). Select that roster scope to change their shift and avoid duplicate assignments.`,
        );
        if (side === "left") setLeftAddEmployeeKey("");
        else setRightAddEmployeeKey("");
        return;
      }

      const employeeKey = `new::${selectedDepartmentId}::${selectedSubDepartmentId}::${employeeType}::${normalizedPersonId}`;
      const newEmployee: RosterEmployee = {
        key: employeeKey,
        personId: selectedEmployee.personId,
        personName: selectedEmployee.personName,
        offDay: selectedEmployee.offDay ?? "",
        subDepartmentId: selectedSubDepartmentId,
        subDepartmentName: selectedSubDepartment?.label ?? "Sub-department",
        employeeType,
        originalShiftId: "",
      };

      setEmployees((previous) => {
        const duplicate = previous.find(
          (employee) =>
            normalizeText(employee.personId) === normalizedPersonId &&
            employee.subDepartmentId === selectedSubDepartmentId &&
            employee.employeeType === employeeType,
        );
        return duplicate ? previous : [...previous, newEmployee];
      });
      setOriginalAssignments((previous) => ({
        ...previous,
        [employeeKey]: "",
      }));
      setAssignments((previous) => ({
        ...previous,
        [employeeKey]: targetShiftId,
      }));
      setRemovedEmployeeKeys((previous) =>
        previous.filter((key) => key !== employeeKey),
      );
      setDraftActionTypes((previous) => ({
        ...previous,
        [employeeKey]: "MOVE",
      }));
      toast.success(
        `${selectedEmployee.personName} added to the draft roster. Review and submit to save.`,
      );
    }

    if (side === "left") {
      setLeftAddEmployeeKey("");
      setLeftPage(0);
    } else {
      setRightAddEmployeeKey("");
      setRightPage(0);
    }
  };

  const resetDraft = () => {
    const savedAssignments = Object.fromEntries(
      Object.entries(originalAssignments).filter(
        ([key]) => !key.startsWith("new::"),
      ),
    );
    setEmployees((previous) =>
      previous.filter((employee) => !employee.key.startsWith("new::")),
    );
    setOriginalAssignments(savedAssignments);
    setAssignments(savedAssignments);
    setRemovedEmployeeKeys([]);
    setDraftActionTypes({});
    setReviewOpen(false);
    toast.info("Local changes have been discarded.");
  };

  const openReview = () => {
    if (!hasDraftChanges) {
      toast.info("There are no shift changes to review yet.");
      return;
    }
    setReviewOpen(true);
  };

  const handleFinalSubmit = async () => {
    if (!hasDraftChanges || finalSubmitting) return;

    if (!selectedSubDepartmentId) {
      toast.error("Select a sub-department before submitting roster changes.");
      return;
    }

    if (!isHrUser && selectedDepartmentId !== currentDepartment?.id) {
      toast.error("Only HR users can manage shifts for another department.");
      return;
    }

    // The save API is a move/remove API: every change must point to the
    // employee's existing source assignment. Never send null/empty source IDs.
    // A person created locally from the global employee directory has no source
    // shift; that needs a backend "new assignment" workflow rather than a MOVE.
    const changesWithoutSource = changedEmployees.filter((employee) => {
      const sourceShiftId =
        originalAssignments[employee.key] || employee.originalShiftId || "";
      return !employee.personId.trim() || !sourceShiftId.trim();
    });

    if (changesWithoutSource.length > 0) {
      const names = changesWithoutSource
        .map(
          (employee) =>
            `${employee.personName} (${employee.personId || "no PersonId"})`,
        )
        .join(", ");
      toast.error(
        `Cannot submit these roster changes because PersonId or FromShiftMasterId is missing: ${names}. Each employee must have an existing source shift for this save API.`,
      );
      return;
    }

    const payload: ShiftRosterChangePayload = {
      departmentId: selectedDepartmentId,
      subDepartmentId: selectedSubDepartmentId,
      employeeType,
      shiftHours,
      scheduleMonth: selectedMonth,
      scheduleYear: selectedYear,
      changes: changedEmployees.map((employee) => {
        const toShiftId = assignments[employee.key] ?? "";
        const removed = removedSet.has(employee.key) || !toShiftId;
        const sourceShiftId =
          originalAssignments[employee.key] || employee.originalShiftId;

        return {
          personId: employee.personId.trim(),
          fromShiftMasterId: sourceShiftId,
          toShiftMasterId: toShiftId || null,
          removed,
          actionType:
            draftActionTypes[employee.key] ?? (removed ? "REMOVE" : "MOVE"),
        };
      }),
    };

    setFinalSubmitting(true);
    try {
      if (onSubmitChanges) {
        await onSubmitChanges(payload);
      } else {
        await SaveShiftRosterChanges(payload);
      }
      setReviewOpen(false);
      await loadData();
      toast.success("Shift roster changes submitted successfully.");
    } catch (error) {
      console.error("Failed to submit shift roster changes:", error);
      toast.error("Unable to save shift roster changes. Please try again.");
    } finally {
      setFinalSubmitting(false);
    }
  };

  const renderEmployeeCard = (
    employee: RosterEmployee,
    side: "left" | "right",
  ) => {
    const targetShiftId =
      side === "left" ? rightShift?.id ?? "" : leftShift?.id ?? "";
    const currentShiftId = assignments[employee.key] ?? "";
    const targetLabel =
      availableShifts.find((shift) => shift.id === targetShiftId)?.label ??
      "other shift";

    return (
      <Paper
        key={employee.key}
        data-roster-employee-card="true"
        draggable
        onDragStart={(event) => {
          event.dataTransfer.setData("text/plain", employee.key);
          event.dataTransfer.effectAllowed = "move";
          setDraggedEmployeeKey(employee.key);
        }}
        onDragEnd={() => setDraggedEmployeeKey("")}
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          p: 1.1,
          border: "1px solid #e6e9ed",
          borderRadius: 1.5,
          background: draggedEmployeeKey === employee.key ? "#fff5f1" : "#fff",
          cursor: "grab",
          transition: "border-color .15s ease, box-shadow .15s ease",
          "&:hover": {
            borderColor: "#ff9a82",
            boxShadow: "0 2px 9px rgba(24,39,75,.06)",
          },
        }}
      >
        <DragIndicatorRoundedIcon
          sx={{ color: "#9aa3af", fontSize: 19, flexShrink: 0 }}
        />
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 13.5,
            fontWeight: 700,
            bgcolor: "#fff0e9",
            color: "#f47759",
          }}
        >
          {shortName(employee.personName)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14.5,
              fontWeight: 650,
              color: "#152238",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {employee.personName}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexWrap: "wrap", columnGap: 0.75, rowGap: 0.5 }}
          >
            <Typography sx={{ fontSize: 13, color: "#718096" }}>
              ID: {employee.personId}
            </Typography>
            {employee.offDay && (
              <Chip
                size="small"
                label={`Off ${employee.offDay}`}
                sx={{
                  height: 20,
                  fontSize: 12.5,
                  bgcolor: "#f2f5f8",
                  color: "#657184",
                }}
              />
            )}
          </Stack>
        </Box>
        <Tooltip title={`Move to ${targetLabel}`}>
          <span>
            <IconButton
              size="small"
              disabled={!targetShiftId || currentShiftId === targetShiftId}
              aria-label={`Move ${employee.personName} to ${targetLabel}`}
              onClick={() => moveEmployee(employee.key, targetShiftId)}
              sx={{
                color: "#f47f62",
                border: "1px solid #ffe0d6",
                borderRadius: 1.25,
              }}
            >
              {side === "left" ? (
                <ArrowForwardRoundedIcon fontSize="small" />
              ) : (
                <ArrowBackRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Remove from this month's roster">
          <IconButton
            size="small"
            aria-label={`Remove ${employee.personName}`}
            onClick={() => removeEmployee(employee.key)}
            sx={{ color: "#98a1ad" }}
          >
            <PersonRemoveOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  };

  const renderShiftPanel = (
    shift: ShiftOption | undefined,
    side: "left" | "right",
  ) => {
    const isLeft = side === "left";
    const search = isLeft ? leftSearch : rightSearch;
    const setSearch = isLeft ? setLeftSearch : setRightSearch;
    const panelPage = isLeft ? leftPage : rightPage;
    const setPanelPage = isLeft ? setLeftPage : setRightPage;
    // The card height is independent so either side can be resized without resizing its partner.
    const cardHeight = isLeft ? leftCardHeight : rightCardHeight;
    const cardFitHeight = isLeft ? leftCardFitHeight : rightCardFitHeight;
    const renderedCardHeight = Math.min(cardHeight, cardFitHeight);
    const setCardHeight = isLeft ? setLeftCardHeight : setRightCardHeight;
    const cardRef = isLeft ? leftCardRef : rightCardRef;
    const employeeListRef = isLeft ? leftEmployeeListRef : rightEmployeeListRef;
    const addEmployeeKey = isLeft ? leftAddEmployeeKey : rightAddEmployeeKey;
    const setAddEmployeeKey = isLeft
      ? setLeftAddEmployeeKey
      : setRightAddEmployeeKey;
    const addEmployeeSearch = isLeft
      ? leftAddEmployeeSearch
      : rightAddEmployeeSearch;
    const setAddEmployeeSearch = isLeft
      ? setLeftAddEmployeeSearch
      : setRightAddEmployeeSearch;
    const addDropdownOpen = isLeft ? leftAddDropdownOpen : rightAddDropdownOpen;
    const setAddDropdownOpen = isLeft
      ? setLeftAddDropdownOpen
      : setRightAddDropdownOpen;
    const rows = shift ? getShiftEmployees(shift.id, search) : [];
    const maxPanelPage = Math.max(
      0,
      Math.ceil(rows.length / panelRowsPerPage) - 1,
    );
    const effectivePanelPage = Math.min(panelPage, maxPanelPage);
    const pagedRows = rows.slice(
      effectivePanelPage * panelRowsPerPage,
      effectivePanelPage * panelRowsPerPage + panelRowsPerPage,
    );
    const employeeCount = shift ? getShiftEmployees(shift.id).length : 0;
    const targetShiftId = shift?.id ?? "";
    const personIdsAlreadyInTarget = new Set(
      employees
        .filter(
          (employee) =>
            !removedSet.has(employee.key) &&
            (assignments[employee.key] ?? "") === targetShiftId,
        )
        .map((employee) => normalizeText(employee.personId))
        .filter(Boolean),
    );
    // Populate from GetAllEmployeeDetailsDropdowns, not from the currently assigned roster.
    // Only omit employees already active in this target shift to avoid duplicates.
    const addCandidates = employeeDirectory.filter(
      (employee) =>
        !personIdsAlreadyInTarget.has(normalizeText(employee.personId)),
    );
    const filteredAddCandidates = addCandidates.filter((employee) => {
      const query = normalizeText(addEmployeeSearch);
      if (!query) return true;
      return normalizeText(
        `${employee.personName} ${employee.personId}`,
      ).includes(query);
    });

    return (
      <Card
        key={side}
        ref={cardRef}
        sx={{
          p: 0,
          overflow: "hidden",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // Height follows the user's resize until the currently displayed employee rows all fit.
          position: "relative",
          height: renderedCardHeight,
          minHeight: Math.min(420, cardFitHeight),
          maxHeight: "none",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: isLeft ? "#ff9278" : "#5e9ed6",
                }}
              />
              <Typography
                sx={{ fontWeight: 750, fontSize: 15, color: "#1d2939" }}
              >
                {shift?.label ??
                  (isLeft ? "Select first shift" : "Select second shift")}
              </Typography>
            </Stack>
          </Box>
          <Chip
            size="small"
            icon={<GroupsOutlinedIcon />}
            label={employeeCount}
            sx={{
              bgcolor: "#fff",
              border: "1px solid #d5eff0",
              fontWeight: 700,
              flexShrink: 0,
            }}
          />
        </Box>

        <Box
          sx={{
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "minmax(0, 1fr) minmax(0, 1fr) auto",
              },
              gap: 0.75,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search by ID or name..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPanelPage(0);
              }}
              disabled={!shift}
              sx={{
                minWidth: 0,
                "& .MuiInputBase-root": {
                  height: 40,
                  bgcolor: "#fff",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{ fontSize: 18, color: "#8a94a3" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setPanelPage(0);
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            <FormControl
              size="small"
              fullWidth
              disabled={
                !shift || employeeDirectoryLoading || addCandidates.length === 0
              }
              sx={{
                minWidth: 0,
                height: 40,
                "& .MuiInputBase-root": {
                  height: "40px !important",
                  minHeight: "40px !important",
                  boxSizing: "border-box",
                  backgroundColor: "#fff",
                },
              }}
            >
              <Select
                displayEmpty
                inputProps={{ "aria-label": "Add / replace employee" }}
                value={addEmployeeKey}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Box
                        component="span"
                        sx={{ color: "#8a94a3", opacity: 1 }}
                      >
                        Add / replace employee
                      </Box>
                    );
                  }

                  const selectedEmployee = employeeDirectory.find(
                    (employee) => employee.personId === selected,
                  );
                  return selectedEmployee
                    ? `${selectedEmployee.personName} · ${selectedEmployee.personId}`
                    : String(selected);
                }}
                open={addDropdownOpen}
                onOpen={() => setAddDropdownOpen(true)}
                onClose={() => {
                  setAddDropdownOpen(false);
                  setAddEmployeeSearch("");
                }}
                onChange={(event) => {
                  setAddEmployeeKey(event.target.value);
                  setAddEmployeeSearch("");
                  setAddDropdownOpen(false);
                }}
                sx={{
                  bgcolor: "#fff",
                  height: "40px !important",
                  minHeight: "40px !important",
                  maxHeight: 40,
                  minWidth: 0,
                  boxSizing: "border-box",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    height: "40px !important",
                    minHeight: "40px !important",
                    py: "0 !important",
                    pr: "32px !important",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                MenuProps={{
                  ...dropdownMenuProps,
                  MenuListProps: {
                    autoFocusItem: false,
                    dense: true,
                  },
                }}
              >
                <ListSubheader
                  component="div"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    bgcolor: "background.paper",
                    px: 1.25,
                    py: 1,
                    lineHeight: "normal",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder="Search by employee name or ID..."
                    value={addEmployeeSearch}
                    onChange={(event) =>
                      setAddEmployeeSearch(event.target.value)
                    }
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      // Keep keystrokes out of Select's option navigation, but
                      // still let Escape reliably close the controlled menu.
                      event.stopPropagation();
                      if (event.key === "Escape") {
                        setAddDropdownOpen(false);
                        setAddEmployeeSearch("");
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: addEmployeeSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear employee search"
                            onClick={(event) => {
                              event.stopPropagation();
                              setAddEmployeeSearch("");
                            }}
                          >
                            <CloseRoundedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    sx={{
                      "& .MuiInputBase-root": { height: 36, fontSize: 14.5 },
                      "& .MuiInputBase-input": { py: 0.5 },
                    }}
                  />
                </ListSubheader>
                {filteredAddCandidates.map((employee) => {
                  const existingRosterEmployee = employees.find(
                    (item) =>
                      normalizeText(item.personId) ===
                        normalizeText(employee.personId) &&
                      !removedSet.has(item.key) &&
                      Boolean(assignments[item.key]),
                  );
                  const currentShift = allShifts.find(
                    (option) =>
                      option.id ===
                      (existingRosterEmployee
                        ? assignments[existingRosterEmployee.key]
                        : ""),
                  );
                  const currentAssignmentLabel = currentShift
                    ? ` (currently: ${currentShift.label}${
                        existingRosterEmployee?.subDepartmentName
                          ? ` · ${existingRosterEmployee.subDepartmentName}`
                          : ""
                      })`
                    : "";

                  return (
                    <MenuItem
                      key={employee.personId}
                      value={employee.personId}
                      sx={{ fontSize: 14.5 }}
                    >
                      {employee.personName} · {employee.personId}
                      {currentAssignmentLabel}
                    </MenuItem>
                  );
                })}
                {!employeeDirectoryLoading &&
                  filteredAddCandidates.length === 0 && (
                    <MenuItem disabled sx={{ fontSize: 14.5 }}>
                      {addCandidates.length === 0
                        ? "No employees available to add"
                        : "No employees match your search"}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              disabled={!shift || !addEmployeeKey}
              onClick={() => {
                setAddDropdownOpen(false);
                setAddEmployeeSearch("");
                addEmployeeToShift(targetShiftId, addEmployeeKey, side);
              }}
              sx={{
                minWidth: 84,
                height: 36,
                px: 1.25,
                textTransform: "none",
                whiteSpace: "nowrap",
                borderColor: "#ffc2b2",
                color: "#f47759",
              }}
            >
              Add
            </Button>
          </Box>

          <Divider />

          <Box
            ref={employeeListRef}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
            }}
            onDrop={(event) => shift && handleDrop(event, shift.id)}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              scrollbarGutter: "stable",
              display: "flex",
              flexDirection: "column",
              gap: 0.9,
              p: 0.5,
              borderRadius: 1.5,
              border:
                draggedEmployeeKey && shift
                  ? "1px dashed #ff9b82"
                  : "1px dashed transparent",
              backgroundColor:
                draggedEmployeeKey && shift ? "#fffaf8" : "transparent",
              transition: "border-color .15s ease, background-color .15s ease",
            }}
          >
            {!shift ? (
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  placeItems: "center",
                  color: "text.secondary",
                  textAlign: "center",
                  p: 2,
                }}
              >
                <Typography fontSize={14.5}>
                  Select two shifts to compare their employees.
                </Typography>
              </Box>
            ) : loading ? (
              <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
                <CircularProgress size={26} />
              </Box>
            ) : rows.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "grid",
                  placeItems: "center",
                  textAlign: "center",
                  p: 2,
                  color: "#8a94a3",
                }}
              >
                <Box>
                  <GroupsOutlinedIcon
                    sx={{ fontSize: 32, opacity: 0.45, mb: 0.5 }}
                  />
                  <Typography
                    sx={{ fontSize: 14.5, fontWeight: 600, color: "#667085" }}
                  >
                    {employeeCount
                      ? "No employees match your search"
                      : "No employees in this shift"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mt: 0.4 }}>
                    Drag an employee here or use Add / replace.
                  </Typography>
                </Box>
              </Box>
            ) : (
              pagedRows.map((employee) => renderEmployeeCard(employee, side))
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "nowrap",
              borderTop: "1px solid #edf0f3",
              mt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 12.5,
                color: "#98a2b3",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <DragIndicatorRoundedIcon sx={{ fontSize: 15 }} />
              Drag employees to opposite panel.
            </Typography>

            {shift && !loading && rows.length > 0 && (
              <TablePagination
                component="div"
                count={rows.length}
                page={effectivePanelPage}
                rowsPerPage={panelRowsPerPage}
                onPageChange={(_, newPage) => setPanelPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setPanelRowsPerPage(Number.parseInt(event.target.value, 10));
                  setLeftPage(0);
                  setRightPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                showFirstButton
                showLastButton
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 52,
                    pl: 1,
                    pr: 0,
                    flexWrap: "nowrap",
                    justifyContent: "flex-end",
                  },
                  "& .MuiTablePagination-spacer": {
                    flex: "0 0 0",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: 13.5,
                      color: "#8290a3",
                      whiteSpace: "nowrap",
                    },
                  "& .MuiTablePagination-select": {
                    fontSize: 13.5,
                  },
                  "& .MuiTablePagination-actions": {
                    ml: 0.5,
                  },
                }}
              />
            )}
          </Box>
        </Box>

        <Tooltip title="Drag to resize card height">
          <Box
            role="separator"
            aria-label={`Resize ${
              isLeft ? "first" : "second"
            } shift card height`}
            aria-orientation="horizontal"
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              setResizeDrag({
                side,
                startY: event.clientY,
                startHeight:
                  cardRef.current?.getBoundingClientRect().height ??
                  renderedCardHeight,
              });
            }}
            onPointerMove={(event) => {
              if (!resizeDrag || resizeDrag.side !== side) return;
              // Stop at the measured height needed to show every employee on
              // this page. The limit grows/shrinks with the current rows, not the viewport.
              const minCardHeight = Math.min(420, cardFitHeight);
              const nextHeight =
                resizeDrag.startHeight + (event.clientY - resizeDrag.startY);
              setCardHeight(
                Math.min(cardFitHeight, Math.max(minCardHeight, nextHeight)),
              );
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
              setResizeDrag((current) =>
                current?.side === side ? null : current,
              );
            }}
            onPointerCancel={() =>
              setResizeDrag((current) =>
                current?.side === side ? null : current,
              )
            }
            onLostPointerCapture={() =>
              setResizeDrag((current) =>
                current?.side === side ? null : current,
              )
            }
            sx={{
              position: "absolute",
              right: 2,
              bottom: 2,
              width: 26,
              height: 26,
              display: "grid",
              placeItems: "center",
              borderRadius: 1,
              color: resizeDrag?.side === side ? "#f47759" : "#8794a5",
              bgcolor:
                resizeDrag?.side === side
                  ? "rgba(255,130,102,.12)"
                  : "rgba(255,255,255,.92)",
              cursor: "ns-resize",
              touchAction: "none",
              userSelect: "none",
              zIndex: 3,
              "&:hover": {
                color: "#f47759",
                bgcolor: "#fff0eb",
              },
            }}
          >
            <DragIndicatorRoundedIcon
              sx={{ fontSize: 19, transform: "rotate(-45deg)" }}
            />
          </Box>
        </Tooltip>
      </Card>
    );
  };

  const breadcrumbItems = [
    { to: "/", title: "ASAI" },
    { title: "Shift Management" },
    { title: "Manage Shift Roster" },
  ];

  const removedCount = scopedEmployees.filter((employee) =>
    removedSet.has(employee.key),
  ).length;
  const leftCount = leftShift ? getShiftEmployees(leftShift.id).length : 0;
  const rightCount = rightShift ? getShiftEmployees(rightShift.id).length : 0;
  const selectedShiftEmployeeCount = scopedEmployees.filter(
    (employee) =>
      selectedShiftIds.includes(assignments[employee.key] ?? "") &&
      !removedSet.has(employee.key),
  ).length;
  const activeMergeSourceIds = mergeSourceShiftIds.filter(
    (id) => id !== mergeTargetShiftId && selectedShiftIds.includes(id),
  );
  const mergeSourceEmployees = scopedEmployees.filter(
    (employee) =>
      activeMergeSourceIds.includes(assignments[employee.key] ?? "") &&
      !removedSet.has(employee.key),
  );
  const mergeSourceEmployeeCount = mergeSourceEmployees.length;
  const mergeTargetPersonIds = new Set(
    scopedEmployees
      .filter(
        (employee) =>
          !removedSet.has(employee.key) &&
          assignments[employee.key] === mergeTargetShiftId,
      )
      .map((employee) => normalizeText(employee.personId))
      .filter(Boolean),
  );
  const mergeUniqueNewEmployeeIds = new Set(
    mergeSourceEmployees
      .map((employee) => normalizeText(employee.personId))
      .filter((personId) => personId && !mergeTargetPersonIds.has(personId)),
  );
  const mergeAlreadyPresentEmployeeIds = new Set(
    mergeSourceEmployees
      .map((employee) => normalizeText(employee.personId))
      .filter((personId) => personId && mergeTargetPersonIds.has(personId)),
  );

  return (
    <PageContainer title="Manage Shift Roster">
      <Breadcrumb title="Manage Shift Roster" items={breadcrumbItems} />
      <Box>
        <Card sx={{ mb: 2, p: 0, overflow: "hidden" }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Typography>Shift Management Filters</Typography>
          </Box>
          <Box
            sx={{
              p: "10px 24px 16px",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "1fr 1.3fr 1fr .8fr 1.4fr .9fr .7fr auto",
              },
              gap: 1.5,
              alignItems: "start",
            }}
          >
            <TextField
              select
              fullWidth
              size="small"
              label="Department"
              value={selectedDepartmentId}
              disabled={
                departmentBootstrapLoading || !departments.length || !isHrUser
              }
              onChange={(event) => {
                const nextDepartmentId = String(event.target.value);
                if (!isHrUser && nextDepartmentId !== currentDepartment?.id) {
                  toast.warning(
                    "Only users whose logged-in department is HR can open another department's roster.",
                  );
                  return;
                }
                if (nextDepartmentId === selectedDepartmentId) return;
                if (
                  hasDraftChanges &&
                  !window.confirm(
                    "Changing department will discard the unsaved roster changes. Continue?",
                  )
                ) {
                  return;
                }
                setSelectedDepartmentId(nextDepartmentId);
                setAssignments({});
                setOriginalAssignments({});
                setEmployees([]);
                setRemovedEmployeeKeys([]);
                setSelectedShiftIdsState(null);
                setLeftShiftId("");
                setRightShiftId("");
                setSelectedSubDepartmentId("");
                setLeftPage(0);
                setRightPage(0);
              }}
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              {departments.map((department) => (
                <MenuItem
                  key={department.id}
                  value={department.id}
                  disabled={
                    !isHrUser && department.id !== currentDepartment?.id
                  }
                >
                  {department.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Sub-department"
              value={selectedSubDepartmentId}
              onChange={(event) =>
                setSelectedSubDepartmentId(event.target.value)
              }
              disabled={!subDepartments.length}
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              {subDepartments.map((subDepartment) => (
                <MenuItem key={subDepartment.id} value={subDepartment.id}>
                  {subDepartment.label}
                  {subDepartment.code ? ` (${subDepartment.code})` : ""}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Employee Type"
              value={employeeType}
              onChange={(event) =>
                setEmployeeType(event.target.value as EmployeeType)
              }
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              {EMPLOYEE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Shift Hours"
              value={shiftHours}
              onChange={(event) =>
                setShiftHours(Number(event.target.value) as ShiftHours)
              }
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              <MenuItem value={10}>10 Hours</MenuItem>
              <MenuItem value={8}>8 Hours</MenuItem>
            </TextField>

            <FormControl
              fullWidth
              size="small"
              sx={{
                ...fieldSx,
                "& .MuiInputBase-root": {
                  height: 40,
                  minHeight: 40,
                  backgroundColor: "#fff",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                  height: "40px !important",
                  minHeight: "40px !important",
                  maxHeight: "40px",
                  overflow: "hidden",
                  py: "0 !important",
                  pr: "32px !important",
                },
              }}
            >
              <InputLabel id="manage-shifts-label">
                Select multiple shifts
              </InputLabel>
              <Select
                labelId="manage-shifts-label"
                multiple
                value={selectedShiftIds}
                label="Select multiple shifts"
                onChange={changeShiftSelection}
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${selectedIds.length} shift${
                          selectedIds.length === 1 ? "" : "s"
                        } selected`}
                        sx={{
                          flexShrink: 0,
                          height: 25,
                          fontSize: 13.5,
                          fontWeight: 600,
                          bgcolor: "#e8f8f9",
                          color: "#226b73",
                          border: "1px solid #cdeef0",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 320,
                      mt: 0.5,
                      border: "1px solid #e4eaef",
                      borderRadius: 1.5,
                      boxShadow: "0 8px 24px rgba(16,24,40,.12)",
                      "& .MuiMenuItem-root": {
                        minHeight: 40,
                        fontSize: 14,
                        py: 0.75,
                        px: 1.5,
                      },
                    },
                  },
                }}
              >
                {availableShifts.map((shift) => {
                  const isSelected = selectedShiftIds.includes(shift.id);
                  return (
                    <MenuItem key={shift.id} value={shift.id}>
                      <Checkbox
                        checked={isSelected}
                        disableRipple
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 1,
                          color: "#aab4c0",
                          "&.Mui-checked": { color: "#ff8266" },
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            lineHeight: 1.35,
                            color: "#253247",
                            whiteSpace: "normal",
                          }}
                        >
                          {shift.label}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              select
              fullWidth
              size="small"
              label="Month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(Number(event.target.value))}
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              {MONTH_OPTIONS.map((month, index) => (
                <MenuItem key={month} value={index + 1}>
                  {month}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
              sx={fieldSx}
              SelectProps={{ MenuProps: dropdownMenuProps }}
            >
              {YEAR_OPTIONS.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              size="small"
              startIcon={
                loading ? (
                  <CircularProgress size={14} />
                ) : (
                  <RefreshRoundedIcon />
                )
              }
              disabled={loading}
              onClick={() => void loadData()}
              sx={{
                height: 40,
                textTransform: "none",
                color: "#ff8266",
                borderColor: "#ffc1b0",
                whiteSpace: "nowrap",
              }}
            >
              Refresh
            </Button>
          </Box>
        </Card>

        {loadError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void loadData()}
                disabled={loading}
              >
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {availableShifts.length === 0 && !loading && !loadError && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No {shiftHours}-hour shifts were returned for this department and
            employee type.
          </Alert>
        )}

        <Card sx={{ mt: 2, p: 0, overflow: "hidden" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Box>
              <Typography sx={{ color: "primary.main" }}>
                Employee Shift Planner
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                disabled={!hasDraftChanges}
                onClick={resetDraft}
                sx={{ textTransform: "none" }}
              >
                Discard changes
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={openReview}
                disabled={!hasDraftChanges}
                sx={{
                  textTransform: "none",
                  boxShadow: "none",
                  bgcolor: "#ff8a70",
                  "&:hover": { bgcolor: "#f47759" },
                }}
              >
                Review changes
              </Button>
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            {selectedShiftIds.length > 2 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    md: "minmax(230px, .85fr) minmax(0, 1.5fr)",
                  },
                  gap: 1.5,
                  alignItems: "center",
                  backgroundColor: "#f2fbfc",
                  border: "1px solid #d9f0f2",
                  borderRadius: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <InfoOutlinedIcon
                    sx={{ color: "#54b9c5", fontSize: 21, mt: "2px" }}
                  />
                  <Box>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 14, color: "#253247" }}
                    >
                      Compare or swap a pair
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13.5, color: "#718096", mt: 0.25 }}
                    >
                      {selectedShiftIds.length} shifts selected. Choose the two
                      shifts to show below.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: "minmax(0, 1fr) minmax(0, 1fr)",
                    },
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="First shift"
                    value={leftShiftId}
                    onChange={(event) => {
                      const value = event.target.value;
                      setLeftShiftId(value);
                      if (value === rightShiftId)
                        setRightShiftId(
                          selectedShiftIds.find((id) => id !== value) ?? "",
                        );
                    }}
                    sx={{ ...fieldSx, minWidth: 0 }}
                    SelectProps={{ MenuProps: dropdownMenuProps }}
                  >
                    {selectedShiftIds.map((id) => (
                      <MenuItem key={id} value={id}>
                        {availableShifts.find((shift) => shift.id === id)
                          ?.label ?? id}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Second shift"
                    value={rightShiftId}
                    onChange={(event) => setRightShiftId(event.target.value)}
                    sx={{ ...fieldSx, minWidth: 0 }}
                    SelectProps={{ MenuProps: dropdownMenuProps }}
                  >
                    {selectedShiftIds
                      .filter((id) => id !== leftShiftId)
                      .map((id) => (
                        <MenuItem key={id} value={id}>
                          {availableShifts.find((shift) => shift.id === id)
                            ?.label ?? id}
                        </MenuItem>
                      ))}
                  </TextField>
                </Box>
              </Box>
            )}

            {selectedShiftIds.length < 2 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Select at least two shifts in the filters to compare employees
                and use the swap action.
              </Alert>
            )}

            <Stack
              direction="row"
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mb: 2, gap: 1 }}
            >
              <Button
                variant="outlined"
                startIcon={<SwapHorizRoundedIcon />}
                disabled={
                  !leftShift ||
                  !rightShift ||
                  leftShift.id === rightShift.id ||
                  loading ||
                  leftCount + rightCount === 0
                }
                onClick={swapAllEmployees}
                sx={{
                  textTransform: "none",
                  borderColor: "#ffad9a",
                  color: "#f47759",
                  fontWeight: 650,
                  px: 2.5,
                }}
              >
                Swap
              </Button>
              <Button
                variant="contained"
                startIcon={<CallMergeRoundedIcon />}
                disabled={
                  selectedShiftIds.length < 2 ||
                  loading ||
                  !selectedSubDepartmentId ||
                  selectedShiftEmployeeCount === 0
                }
                onClick={openMergeDialog}
                sx={{
                  textTransform: "none",
                  bgcolor: "primary.main",
                  color: "#fff",
                  fontWeight: 650,
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "secondary.main", boxShadow: "none" },
                }}
              >
                Merge
              </Button>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  lg: "minmax(0, 1fr) 58px minmax(0, 1fr)",
                },
                gap: { xs: 1.5, lg: 1 },
                // Avoid CSS grid stretching the collapsed card to match the expanded one.
                alignItems: "start",
              }}
            >
              {renderShiftPanel(leftShift, "left")}
              <Box
                sx={{
                  // Stretch only the middle column so its swap icon centers against
                  // the full height of the two cards, without stretching either card.
                  display: { xs: "none", lg: "flex" },
                  alignSelf: "stretch",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    bgcolor: "#fff1ec",
                    border: "1px solid #ffddD3",
                    display: "grid",
                    placeItems: "center",
                    color: "#f47759",
                  }}
                >
                  <SwapHorizRoundedIcon />
                </Box>
              </Box>
              {renderShiftPanel(rightShift, "right")}
            </Box>
          </Box>
        </Card>
      </Box>

      <Dialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: 750,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CallMergeRoundedIcon sx={{ color: "#327f88" }} />
          Merge shifts into one roster
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14.5, color: "text.secondary", mb: 1.5 }}>
            {selectedDepartmentId
              ? departments.find((item) => item.id === selectedDepartmentId)
                  ?.label
              : "Department"}{" "}
            · {selectedSubDepartment?.label ?? "Selected sub-department"} ·{" "}
            {employeeTypeLabel(employeeType)} · {shiftHours}-hour shifts ·{" "}
            {getMonthYearLabel(selectedMonth, selectedYear)}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, fontWeight: 700, color: "#344054", mb: 1 }}
          >
            Source shifts (select shifts to merge into the target)
          </Typography>
          <Stack spacing={1}>
            {selectedShiftIds
              .filter((id) => id !== mergeTargetShiftId)
              .map((id) => {
                const shift = availableShifts.find((item) => item.id === id);
                const isSourceSelected = activeMergeSourceIds.includes(id);
                const employeeCount = scopedEmployees.filter(
                  (employee) =>
                    assignments[employee.key] === id &&
                    !removedSet.has(employee.key),
                ).length;
                return (
                  <Paper
                    key={id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.5,
                      pr: 1.25,
                      borderRadius: 1.5,
                      borderColor: isSourceSelected ? "#8bc9cf" : "#e5eaf0",
                      bgcolor: isSourceSelected ? "#f2fbfc" : "#fff",
                    }}
                  >
                    <Checkbox
                      checked={isSourceSelected}
                      onChange={(event) => {
                        setMergeSourceShiftIds((previous) =>
                          event.target.checked
                            ? Array.from(new Set([...previous, id]))
                            : previous.filter((sourceId) => sourceId !== id),
                        );
                      }}
                      size="small"
                      sx={{
                        color: "#98a2b3",
                        "&.Mui-checked": { color: "#327f88" },
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 14.5,
                          fontWeight: 650,
                          color: "#253247",
                        }}
                      >
                        {shift?.label ?? `Shift ${id}`}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "text.secondary" }}
                      >
                        {employeeCount} employee{employeeCount === 1 ? "" : "s"}{" "}
                        currently assigned
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={employeeCount}
                      sx={{
                        fontSize: 12.5,
                        bgcolor: "#eef2f6",
                        color: "#475467",
                      }}
                    />
                  </Paper>
                );
              })}
          </Stack>

          <TextField
            select
            fullWidth
            size="small"
            label="Target shift (keep this shift)"
            value={mergeTargetShiftId}
            onChange={(event) => {
              const nextTargetId = event.target.value;
              setMergeTargetShiftId(nextTargetId);
              // When the destination changes, default all other selected shifts
              // to sources again; the user can then deselect any source they want to keep.
              setMergeSourceShiftIds(
                selectedShiftIds.filter((id) => id !== nextTargetId),
              );
            }}
            sx={{ mb: 2, mt: 2, ...fieldSx }}
            SelectProps={{ MenuProps: dropdownMenuProps }}
          >
            {selectedShiftIds.map((id) => {
              const shift = availableShifts.find((item) => item.id === id);
              return (
                <MenuItem key={id} value={id}>
                  {shift?.label ?? `Shift ${id}`}
                </MenuItem>
              );
            })}
          </TextField>

          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 14.5, color: "text.secondary" }}>
              New unique employees to add
            </Typography>
            <Typography
              sx={{ fontWeight: 750, fontSize: 18, color: "#327f88" }}
            >
              {mergeUniqueNewEmployeeIds.size}
            </Typography>
          </Box>
          {mergeAlreadyPresentEmployeeIds.size > 0 && (
            <Typography
              sx={{
                display: "block",
                mt: 1,
                fontSize: 13.5,
                color: "text.secondary",
              }}
            >
              {mergeAlreadyPresentEmployeeIds.size} employee(s) from the source
              already exist in the target and will not be duplicated.
            </Typography>
          )}
          {activeMergeSourceIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Select at least one source shift to merge.
            </Alert>
          )}
          {activeMergeSourceIds.length > 0 &&
            mergeSourceEmployeeCount === 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                The selected source shifts have no employees in the current
                sub-department and employee type.
              </Alert>
            )}
          {activeMergeSourceIds.length > 0 &&
            mergeSourceEmployeeCount > 0 &&
            mergeUniqueNewEmployeeIds.size === 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                All source employees are already assigned to the target. The
                merge will consolidate source assignments without creating
                duplicate target assignments.
              </Alert>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setMergeDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CallMergeRoundedIcon />}
            disabled={
              !mergeTargetShiftId ||
              activeMergeSourceIds.length === 0 ||
              mergeSourceEmployeeCount === 0 ||
              loading
            }
            onClick={handleMergeShifts}
            sx={{
              textTransform: "none",
              bgcolor: "#327f88",
              boxShadow: "none",
              "&:hover": { bgcolor: "#266870", boxShadow: "none" },
            }}
          >
            Merge {mergeSourceEmployeeCount} employee
            {mergeSourceEmployeeCount === 1 ? "" : "s"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 750 }}>Review shift changes</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14.5, color: "text.secondary", mb: 1.5 }}>
            {selectedSubDepartment?.label ?? "Selected sub-department"} ·{" "}
            {employeeTypeLabel(employeeType)} ·{" "}
            {getMonthYearLabel(selectedMonth, selectedYear)}
          </Typography>
          {changedEmployees.length === 0 ? (
            <Alert severity="info">
              No changes in the selected sub-department.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {changedEmployees.map((employee) => {
                const fromShift = availableShifts.find(
                  (shift) =>
                    shift.id === (originalAssignments[employee.key] ?? ""),
                );
                const toShiftId = assignments[employee.key] ?? "";
                const toShift = availableShifts.find(
                  (shift) => shift.id === toShiftId,
                );
                const removed = removedSet.has(employee.key) || !toShiftId;
                return (
                  <Paper
                    key={employee.key}
                    variant="outlined"
                    sx={{ p: 1.25, borderRadius: 1.5 }}
                  >
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>
                      {employee.personName}{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 13.5, color: "text.secondary" }}
                      >
                        ({employee.personId})
                      </Typography>
                    </Typography>
                    <Typography
                      sx={{ mt: 0.35, fontSize: 13.5, color: "#667085" }}
                    >
                      {fromShift?.label ?? "Unassigned"} →{" "}
                      {removed
                        ? "Removed from roster"
                        : toShift?.label ?? "Unassigned"}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Review the changes above, then select Final Submit. Database saving
            will work once this page is connected to the roster-save API.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setReviewOpen(false)}
            disabled={finalSubmitting}
            sx={{ textTransform: "none" }}
          >
            Continue editing
          </Button>
          <Button
            onClick={() => void handleFinalSubmit()}
            variant="contained"
            disabled={!hasDraftChanges || finalSubmitting}
            startIcon={
              finalSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              textTransform: "none",
              bgcolor: "#ff8a70",
              boxShadow: "none",
              "&:hover": { bgcolor: "#f47759" },
            }}
          >
            {finalSubmitting ? "Submitting..." : "Final Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
